import { getDeviceInfo } from "./native/nativeHelpers";
import { CancelablePromise as Promise } from "./cancelablePromise";
import { getServerDate } from "./storage/date";
import { DriveStore } from "../store/reducers";
import { MobileActions } from "../store/mobile/actions";
import { DataActions } from "../store/data/actions";
import Api from "../services/api";
import { handleApi } from "./error";
import { isNoVehicle, getCurrentVehicle } from "./device";

/**
 * Get a single GPS position/speed and set status:gps
 * @param options The PositionOptions
 */
export function getCurrentPosition(store?: DriveStore, options: PositionOptions = {}): Promise<GeolocationPosition> {
    let newOptions = {
        ...{
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
        },
        ...options
    } as PositionOptions;

    return new Promise<GeolocationPosition>((resolve, reject: (err?: GeolocationPositionError) => void) => {
        navigator.geolocation.getCurrentPosition(
            (position: GeolocationPosition) => {
                store && store.dispatch(MobileActions.setGps({ isConnected: true, position: position }));
                resolve(position);
            },
            (err: GeolocationPositionError) => {
                store && store.dispatch(MobileActions.setGps({ isConnected: false, position: undefined }));
                reject(err);
            },
            newOptions
        );
    }).timeout(newOptions.timeout as number);
}

/**
 * Gets the last position of the mobile device either from app state or GPS
 */
export function getLastPosition(store?: DriveStore, options: PositionOptions = {}, maxAgeMs = 60000, lastPosition?): Promise<GeolocationPosition> {
    let position = lastPosition || (store ? <GeolocationPosition>store.getState().mobile.lastPosition : null);

    let isPositionRecent = (prevPosition: GeolocationPosition, ageThreshold: number): boolean => {
        return ageThreshold >= Math.abs(getServerDate().getTime() - prevPosition.timestamp) && !(window.DRIVE_DEBUG && window.DRIVE_DEBUG.DISABLE_USING_CACHED_GPS);
    };

    return new Promise<GeolocationPosition>((resolve, reject) => {
        if (position && isPositionRecent(position, maxAgeMs)) {
            resolve(position);
            return;
        }

        getCurrentPosition(store, {
            ...{
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0
            },
            ...options
         } as PositionOptions).then(resolve).catch(reject);
    });
}

export function getBrowserGeoUri(lat, lon, label, successCallback: (uri: string) => void) {
    let thisLabel: string;
    if (window.device) {
        let info = getDeviceInfo();
        let device = info && info.platform;

        thisLabel = typeof label === "undefined" ? "" : encodeURIComponent(`(${ label })`);

        if (device === "iOS") {
            successCallback(`https://maps.apple.com/?q=${ thisLabel }&ll=${ lat },${ lon }&z=17`);
        } else {
            successCallback(`geo:0,0?q=${ lat },${ lon }${ thisLabel }`);
        }
    } else {
        successCallback(`http://maps.google.com/maps?q=loc:${ lat },${ lon }`);
    }
}

/**
 * Gets vehicle location exclusively for NoDeviceId
 * For all other devices it returns `null` because those
 * are populated from the server's data
 * @param vehicle The vehicle object
 * @param force Get location regardless of vehicle
 */
export function getMobileLocation(vehicle?: IIdEntity, force = false, store?: DriveStore) {
    return new Promise<IAddressLookupData | null>((resolve) => {
        if (vehicle && vehicle.id !== "NoDeviceId" && !force) {
            // Log is not for no device, we don't need to set location
            resolve(null);
            return;
        }

        // If no device, we need to get get the current user's location as the log location
        getCurrentPosition(store).then((position: GeolocationPosition) => {
            if (position.coords.accuracy > 1000) {
                // Location was found but likely inaccurate, resolve but don't set location
                resolve(null);
                return;
            }

            let currentLocation: IAddressLookupData = {
                location: {
                    x: position.coords.longitude,
                    y: position.coords.latitude
                }
            };
            resolve(currentLocation);

        }).catch(() => {
            // Location can't be found, resolve but don't set location
            resolve(null);
        });
    });
}

/**
 * Geocodes the address array that is passed in, returns coordinates array.
 * Will either have X/Y coordinates or null if address can't be found.
 * @param addresses Array of addresses that need to be geocoded
 */
export function geocodeAddresses(api, addresses: string[] = []) {
    return new Promise<any>((resolve, reject) => {
        if (addresses.length === 0) {
            resolve(addresses);
        } else {
            api.call("GetCoordinates", { addresses }, resolve, reject);
        }
    });
}

/**
 * Reads the device's current position from DeviceStatusInfo. Resolves undefined when the device is not
 * communicating or reports no position, and rejects when the API call fails.
 */
function requestDeviceStatusLocation(api: Api, deviceId: string): Promise<ICoordinate | undefined> {
    return new Promise<ICoordinate | undefined>((resolve, reject) => {
        api.call("Get", {
            typeName: "DeviceStatusInfo",
            search: {
                deviceSearch: { id: deviceId }
            },
            propertySelector: {
                fields: ["exceptionEvents", "statusData"],
                isIncluded: false
            }
        }, (deviceStatusInfo) => {
            let deviceInfo = deviceStatusInfo && deviceStatusInfo[0];
            if (deviceInfo && deviceInfo.isDeviceCommunicating && deviceInfo.longitude && deviceInfo.latitude) {
                resolve({ x: deviceInfo.longitude, y: deviceInfo.latitude });
                return;
            }
            resolve(undefined);
        }, reject);
    });
}

function getLocationByDeviceStatusInfo(api: Api, store: DriveStore, deviceId?: string) {
    return new Promise<ICoordinate | undefined>(resolve => {
        let targetDeviceId = deviceId;
        const isAssignedVehicle = !targetDeviceId;
        if (!targetDeviceId) {
            let currentVehicle = getCurrentVehicle();
            if (isNoVehicle() || !currentVehicle) {
                resolve(undefined);
                return;
            }
            targetDeviceId = currentVehicle.id;
        } else if (targetDeviceId === "NoDeviceId") {
            resolve(undefined);
            return;
        }

        // The lastVehicleLocation cache is keyed to the driver's assigned vehicle (driving state
        // controls invalidation). Skip it for arbitrary device lookups so we don't return a
        // stale assigned-vehicle position for a different inspected asset.
        if (isAssignedVehicle) {
            let state = store.getState();
            let isDriving = state.data.driving;
            let cachedDeviceLocation = store.getState().data.lastVehicleLocation;
            if (!isDriving) {
                if (cachedDeviceLocation) {
                    resolve(cachedDeviceLocation);
                    return;
                }
            } else {
                store.dispatch(DataActions.setVehicleLocation(undefined));
            }
        }

        requestDeviceStatusLocation(api, targetDeviceId).then(location => {
            if (isAssignedVehicle && location) {
                let isDriving = store.getState().data.driving;
                !isDriving && store.dispatch(DataActions.setVehicleLocation(location));
            }
            resolve(location);
        }, e => {
            e && handleApi(e);
            resolve();
        });
    });
}

/**
 * Reads a live position for an inspected asset from its telematics device only — no phone GPS fallback and
 * no cached vehicle location, so the result is where the asset is now. Rejects when the device cannot be
 * reached within timeoutMs; resolves undefined when it reports no current position.
 */
export function getAssetLocation(api: Api, deviceId: string, timeoutMs: number): Promise<ICoordinate | undefined> {
    return requestDeviceStatusLocation(api, deviceId).timeout(timeoutMs);
}

export function getCurrentLocationUsingDeviceLocationAndGps(
    api: Api,
    store: DriveStore,
    deviceId?: string
): Promise<IAddressLookupData | undefined> {
    return new Promise((resolve, reject) => {
        getLocationByDeviceStatusInfo(api, store, deviceId)
            .then(location => {
                if (location) {
                    resolve({ location });
                    return;
                }
                return getLastPosition(store)
                    .then(position => resolve({ location: { x: position.coords.longitude, y: position.coords.latitude }}))
                    .catch(() => resolve(undefined));
            })
            .catch(reject);
    });
}